import clsx from 'clsx';
import * as React from 'react';
import { SectionList } from 'react-native';
import type { SetterOrUpdater } from 'recoil';

import { Divider, LoadingIndicator, useScreenType } from '@gitbook/spine-react';
import { useEventCallback, useMapRefs } from '@gitbook/util-react-hooks';
import { isPromise } from '@gitbook/util-ts';

import { sizes, spacing } from '../../constants';
import { getSectionListItemLayout, useListKeyboardNavigation } from '../../Lists';
import { executeCommandInPalette, openDescendantInPalette } from '../transforms';
import type {
    ComputedCommandsSection,
    PaletteCommandDef,
    PaletteDef,
    PaletteGroupDef,
} from '../types';
import { getPaletteCommandItemHeight, PaletteCommandItem } from './PaletteCommandItem';
import type { ComputedPaletteCommands } from './useComputePaletteCommands';

/**
 * Listing of commands in a palette.
 */
export function PaletteCommandsList(props: {
    /** Palette to render. */
    palette: PaletteDef;
    /** Computed list of commands to display. */
    computed: ComputedPaletteCommands;
    /** If false, keyboard interactions will be disabled */
    withKeyboardInteractions: boolean;
    /** If true, hover interactions that open descendant palettes will be disabled */
    isAnimatingIn?: boolean;
    /** Callback to update the palette when executing commands. */
    setPalette: SetterOrUpdater<PaletteDef | null>;
    /** Callback to update the top palette when executing commands. */
    setTopPalette: SetterOrUpdater<PaletteDef | null>;
}): React.ReactElement | null {
    const {
        computed: { listId, items, sections, areMoreLoading },
        withKeyboardInteractions,
        isAnimatingIn = false,
        palette,
        setPalette,
        setTopPalette,
    } = props;
    const { isDesktop } = useScreenType();
    const listRef = React.useRef<SectionList<PaletteCommandDef>>(null);
    const getCommandRef = useMapRefs<PaletteCommandDef, HTMLDivElement>();

    const updateDescendantPalette = useEventCallback((command: PaletteCommandDef) => {
        setPalette((current) => {
            if (!current) {
                return null;
            }

            const anchorRef = getCommandRef(command);
            const result = openDescendantInPalette(current, command, anchorRef);

            if (result) {
                return result;
            }

            if (current.descendant) {
                // Close descendant if the current command doesn't have one
                return { ...current, descendant: undefined };
            }

            return current;
        });
    });

    /**
     * Apply a palette result.
     * Merges descendant if provided, otherwise sets the result or closes the palette.
     */
    const applyPaletteResult = useEventCallback(
        (
            result: PaletteDef | null | Promise<PaletteDef | null>,
            descendantToMerge?: PaletteDef | null
        ) => {
            const applyResult = (value: PaletteDef | null) => {
                if (value && descendantToMerge) {
                    setPalette({
                        ...value,
                        descendant: descendantToMerge.descendant,
                    });
                } else if (value) {
                    setPalette(value);
                } else {
                    setTopPalette(null);
                }
            };

            if (isPromise(result)) {
                result.then(applyResult);
            } else {
                applyResult(result);
            }
        }
    );

    const executeCommand = useEventCallback((command: PaletteCommandDef) => {
        // If command has a descendant but no trigger, only open the descendant.
        if (command.descendant && !command.trigger) {
            updateDescendantPalette(command);
            return;
        }

        // If command has both descendant and trigger, update descendant first
        // and then merge it with the result from executeCommandInPalette
        if (command.descendant && command.trigger) {
            const anchorRef = getCommandRef(command);
            const descendantResult = openDescendantInPalette(palette, command, anchorRef);
            const result = executeCommandInPalette(palette, command);
            return applyPaletteResult(result, descendantResult);
        }

        const result = executeCommandInPalette(palette, command);
        return applyPaletteResult(result);
    });

    const closePalette = useEventCallback(() => setPalette(null));

    const getCommandScrollLocation = useEventCallback((key: string) => {
        let itemIndex = -1;
        const sectionIndex = sections.findIndex((section) => {
            itemIndex = section.data.findIndex((c) => c.key === key);
            return itemIndex >= 0;
        });

        return {
            sectionIndex,
            itemIndex,
            viewPosition: 0.5,
        };
    });

    const scrollTo = useEventCallback((command: PaletteCommandDef) =>
        listRef.current?.scrollToLocation(getCommandScrollLocation(command.key))
    );

    const { hoverItem, cursor, onMouseMove } = useListKeyboardNavigation({
        active: withKeyboardInteractions,
        items,
        onSelect: executeCommand,
        onOpen: updateDescendantPalette,
        onClose: closePalette,
        scrollTo,
        startAtFirst: true,
    });

    const handleOverInCommand = useEventCallback((command: PaletteCommandDef) => {
        hoverItem(command);
        // Don't execute the hover/focus callback for items until animation finished, to prevent the animation from causing descendant palettes to open prematurely.
        if (!isAnimatingIn) {
            // We update it even if the command.descendant is undefined, because
            // we need to clear a potential other descendant palette.
            updateDescendantPalette(command);
        }
    });

    /*
     * Get the height of a command item.
     */
    const getItemLayout = React.useMemo(
        () =>
            getSectionListItemLayout<PaletteCommandDef>({
                getItemHeight: ({ item }) => getPaletteCommandItemHeight(item),
                getSectionSeparatorHeight: ({ trailingItem, trailingSection }) =>
                    // see CommandsSectionDivider
                    trailingSection && !trailingItem ? sizes.S / 4 : 0,
                getSectionHeaderHeight: ({ section }) => (section.group.title ? sizes.S : 0),
            }),
        []
    );

    const renderSectionHeader = React.useMemo(
        () =>
            ({ section }: { section: ComputedCommandsSection }) => {
                if (section.group.title) {
                    return <CommandsSectionHeader group={section.group} />;
                }

                return null;
            },
        []
    );

    const renderCommandsSectionDivider = React.useCallback(
        (props: {
            leadingSection: ComputedCommandsSection | void;
            trailingSection: ComputedCommandsSection | void;
            section: ComputedCommandsSection;
            leadingItem: PaletteCommandDef | void;
            trailingItem: PaletteCommandDef | void;
        }) => <CommandsSectionDivider {...props} />,
        []
    );

    const cursorKey = cursor.current?.key;
    const renderCommandItem = React.useCallback(
        ({ item }: { item: PaletteCommandDef }) => (
            <PaletteCommandItem
                ref={getCommandRef(item)}
                active={isDesktop && item.key === cursorKey}
                command={item}
                query={palette.query.text}
                executeCommand={executeCommand}
                onHoverIn={handleOverInCommand}
            />
        ),
        [
            cursorKey,
            executeCommand,
            getCommandRef,
            handleOverInCommand,
            palette.query.text,
            isDesktop,
        ]
    );

    const withLoading = areMoreLoading;

    if (!withLoading && items.length === 0) {
        // When controlled, we always render a placeholder to avoid an empty palette.
        const queryStartsAsUrl =
            palette.query.text.startsWith('http') || palette.query.text.startsWith('www');

        if (queryStartsAsUrl) {
            return <CommandsPlaceholder kind="url" placeholder={palette.query.text} />;
        }

        const placeholder = palette.placeholder || 'No items found';

        return placeholder ? <CommandsPlaceholder placeholder={placeholder} /> : null;
    }

    return (
        <div className="flex h-full flex-1 overflow-y-auto" onMouseMove={onMouseMove}>
            <SectionList<PaletteCommandDef>
                // key is needed here because SectionList is sometimes showing duplicate or stale entries
                // so we use key to completely refresh the SectionList whenever we have a new list to render
                key={listId}
                ref={listRef}
                sections={sections}
                extraData={cursor}
                getItemLayout={getItemLayout}
                renderItem={renderCommandItem}
                SectionSeparatorComponent={renderCommandsSectionDivider}
                renderSectionHeader={renderSectionHeader}
                contentContainerStyle={[spacing.Padding.vertical100]}
                ListFooterComponent={
                    withLoading ? (
                        <div className="flex justify-center p-4">
                            <LoadingIndicator size="large" />
                        </div>
                    ) : null
                }
            />
        </div>
    );
}

/**
 * Blankslate when there are no commands matching.
 */
function CommandsPlaceholder(props: {
    placeholder: string;
    kind?: 'url' | 'default';
}): React.ReactElement {
    return (
        <div
            className={clsx(
                'p-4 text-muted text-ui-base',
                props.kind === 'url' ? 'text-left' : 'text-center'
            )}
        >
            {props.placeholder}
        </div>
    );
}

/**
 * Header for a group of commands.
 */
function CommandsSectionHeader(props: { group: PaletteGroupDef }): React.ReactElement {
    return (
        <div className="flex h-8 items-center px-3 font-medium text-muted text-ui-small uppercase">
            {props.group.title}
        </div>
    );
}

/**
 * Separator between two groups of commands.
 */
function CommandsSectionDivider(props: {
    leadingSection: ComputedCommandsSection | void;
    trailingSection: ComputedCommandsSection | void;
    section: ComputedCommandsSection;
    leadingItem: PaletteCommandDef | void;
    trailingItem: PaletteCommandDef | void;
}): React.ReactElement | null {
    const { trailingSection, trailingItem } = props;

    if (trailingSection && !trailingItem) {
        return <Divider />;
    }

    return null;
}
